import React, { useState, useEffect } from "react";
import { Form, Button, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import BilingualText from "../BilingualText/BilingualText";
import {
	CompanySearchSelect,
	BranchSearchSelect,
	DepositorSearchSelect,
    PartnerSearchSelect,
} from "../SearchSelects";
import {
	getCurrentCompanyId,
	getCurrentBranchId,
	shouldHideBranchField,
} from "../../../util/contextHelper";
import "react-datepicker/dist/react-datepicker.css";

const DepositsFilterForm = ({ fields = [], onSubmit, align = false, defaultCol = { md: 4, lg: 3 }, stackFields = false }) => {
	const navigate = useNavigate();
	const contextCompanyId = getCurrentCompanyId();
	const contextBranchId = getCurrentBranchId();

	const [formData, setFormData] = useState({});
	const [previousCompanyId, setPreviousCompanyId] = useState("");

	// Auto-populate companyId and branchId from context on mount
	useEffect(() => {
		if (contextCompanyId) {
			setFormData((prev) => ({
				...prev,
				companyId: contextCompanyId,
				branchId: contextBranchId || prev.branchId,
			}));
			setPreviousCompanyId(contextCompanyId);
		}
	}, [contextCompanyId, contextBranchId]);

	const handleInputChange = (name, valueOrEvent) => {
		// Handle both direct values and event objects
		let actualValue;
		if (
			valueOrEvent &&
			typeof valueOrEvent === "object" &&
			valueOrEvent.target
		) {
			// It's an event object
			actualValue = valueOrEvent.target.value;
		} else {
			// It's a direct value
			actualValue = valueOrEvent;
		}

		if (name === "loanApplicationNumber") {
			actualValue = String(actualValue || "").toUpperCase();
		}

		setFormData((prev) => {
			const newData = {
				...prev,
				[name]: actualValue,
			};

			// Clear branchId only when companyId actually changes to a different value
			if (name === "companyId") {
				if (previousCompanyId && previousCompanyId !== actualValue) {
					newData.branchId = "";
				}
				setPreviousCompanyId(actualValue);
			}

			console.log("newData", newData);
			return newData;
		});
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		if (typeof onSubmit === 'function') onSubmit(formData);
	};

	const handleReset = () => {
		const resetData = {
			companyId: contextCompanyId || "",
			branchId: contextBranchId || "",
		};
		setFormData(resetData);
		setPreviousCompanyId(contextCompanyId || "");
		// Remove query parameters from URL
		navigate(window.location.pathname, { replace: true });
		onSubmit(resetData);
	};

	const renderField = (field) => {
		const {
			name,
			label,
			type,
			options = [],
			placeholder,
			required = false,
			render,
		} = field;
		const value = formData[name] || "";

		// Custom render function for custom field types
		if (type === "custom" && typeof render === "function") {
			return (
				<Form.Group key={name} className="mb-3">
					{render({
						value,
						onChange: (val) => handleInputChange(name, val),
						name,
						label,
						placeholder,
						required,
					})}
				</Form.Group>
			);
		}

		switch (type) {
			case "branchSearchSelect":
				// Hide branch field for ADMIN, STAFF, CASHIER - value is auto-populated from context
				if (shouldHideBranchField()) {
					return null;
				}
				return (
					<Form.Group key={name} className="mb-3">
						<BranchSearchSelect
							key={`branch-${formData.companyId || "no-company"}`}
							value={value}
							onChange={(valueOrEvent) => handleInputChange(name, valueOrEvent)}
							name={name}
							label={label}
							companyId={formData.companyId || ""}
							placeholder={placeholder || `Search and select ${label}...`}
							required={required}
							allowClear={true}
							showLabel={true}
						/>
					</Form.Group>
				);

			case "companySearchSelect":
				// Don't render company field - value is auto-populated from context
				// Users can change company in header instead
				return null;

			case "depositorSearchSelect":
				return (
					<Form.Group key={name} className="mb-3">
						<DepositorSearchSelect
							key={`depositor-${formData.branchId || "no-branch"}`}
							value={value}
							onChange={(valueOrEvent) => handleInputChange(name, valueOrEvent)}
							name={name}
							label={label}
							branchId={formData.branchId || ""}
							placeholder={placeholder || `Search and select ${label}...`}
							required={required}
							allowClear={true}
							showLabel={true}
						/>
					</Form.Group>
				);

			case "partnerSearchSelect":
				return (
					<Form.Group key={name} className="mb-3">
						<PartnerSearchSelect
							key={`partner-${formData.branchId || "no-branch"}`}
							value={value}
							onChange={(valueOrEvent) => handleInputChange(name, valueOrEvent)}
							name={name}
							label={label}
							companyId={formData.companyId || ""}
							branchId={formData.branchId || ""}
							placeholder={placeholder || `Search and select ${label}...`}
							required={required}
							allowClear={true}
							showLabel={true}
						/>
					</Form.Group>
				);

			case "select":
				return (
					<Form.Group key={name} className="mb-3">
						<Form.Label>
							{label} {required && <span className="text-danger">*</span>}
						</Form.Label>
						<Form.Select
							className="form-control"
							value={value}
							onChange={(e) => handleInputChange(name, e.target.value)}
						>
							<option value="">Select {label}</option>
							{options.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</Form.Select>
					</Form.Group>
				);

			case "date":
				return (
					<Form.Group key={name} className="mb-3">
						<Form.Label>
							{label} {required && <span className="text-danger">*</span>}
						</Form.Label>
						<DatePicker
							selected={value ? new Date(`${value}T00:00:00`) : null}
							onChange={(date) => {
								if (!date) {
									handleInputChange(name, "");
									return;
								}
								const y = date.getFullYear();
								const m = String(date.getMonth() + 1).padStart(2, "0");
								const d = String(date.getDate()).padStart(2, "0");
								handleInputChange(name, `${y}-${m}-${d}`);
							}}
							dateFormat="dd/MM/yyyy"
							placeholderText={placeholder || `Select ${label}`}
							className="form-control"
							maxDate={new Date()}
						/>
					</Form.Group>
				);

			case "number":
				return (
					<Form.Group key={name} className="mb-3">
						<Form.Label>
							{label} {required && <span className="text-danger">*</span>}
						</Form.Label>
						<Form.Control
							type="number"
							placeholder={placeholder || `Enter ${label}`}
							value={value}
							onChange={(e) => handleInputChange(name, e.target.value)}
						/>
					</Form.Group>
				);

			case "email":
				return (
					<Form.Group key={name} className="mb-3">
						<Form.Label>
							{label} {required && <span className="text-danger">*</span>}
						</Form.Label>
						<Form.Control
							type="email"
							placeholder={placeholder || `Enter ${label}`}
							value={value}
							onChange={(e) => handleInputChange(name, e.target.value)}
						/>
					</Form.Group>
				);

			default: // text
				return (
					<Form.Group key={name} className="mb-3">
						<Form.Label>
							{label} {required && <span className="text-danger">*</span>}
						</Form.Label>
						<Form.Control
							type="text"
							placeholder={placeholder || `Enter ${label}`}
							value={value}
							onChange={(e) => handleInputChange(name, e.target.value)}						style={name === "loanApplicationNumber" ? { textTransform: "uppercase" } : undefined}						/>
					</Form.Group>
				);
		}
	};

	if (stackFields) {
		return (
			<Form onSubmit={handleSubmit}>
				{fields.map((field) => {
					const renderedField = renderField(field);
					if (renderedField === null) return null;
					return (
						<Row key={field.name} className="mb-2">
							<Col md={12} lg={12}>
								{renderedField}
							</Col>
						</Row>
					);
				})}

				<Row className="mt-2">
					<Col className="d-flex justify-content-end gap-2">
						<Button
							variant="secondary"
							onClick={handleReset}
							className="d-flex align-items-center text-white btn-rounded"
						>
							<i className="fas fa-redo me-2"></i>
							<BilingualText translationKey="common.reset" />
						</Button>
						<Button
							variant="primary"
							type="submit"
							className="d-flex align-items-center text-white btn-rounded"
						>
							<i className="fas fa-search me-2"></i>
							<BilingualText translationKey="common.search" />
						</Button>
					</Col>
				</Row>
			</Form>
		);
	}

	return (
		<Form onSubmit={handleSubmit}>
			<Row>
				{fields.map((field) => {
					const renderedField = renderField(field);
					// Skip rendering if field returns null (e.g., company field)
					if (renderedField === null) return null;
					const colProps = field.colProps || defaultCol || { md: 4, lg: 3 };
					return (
						<Col key={field.name} {...colProps}>
							{renderedField}
						</Col>
					);
				})}

				{align && (
					<Col {...(defaultCol || { md: 4, lg: 3 })} className="d-flex align-items-center justify-content-end gap-2">
						<Button
							variant="secondary"
							onClick={handleReset}
							className="d-flex align-items-center text-white btn-rounded"
						>
							<i className="fas fa-redo me-2"></i>
							<BilingualText translationKey="common.reset" />
						</Button>
						<Button
							variant="primary"
							type="submit"
							className="d-flex align-items-center text-white btn-rounded"
						>
							<i className="fas fa-search me-2"></i>
							<BilingualText translationKey="common.search" />
						</Button>
					</Col>
				)}
			</Row>

			{!align && (
				<Row>
					<Col className="d-flex justify-content-end gap-2">
						<Button
							variant="secondary"
							onClick={handleReset}
							className="d-flex align-items-center text-white btn-rounded"
						>
							<i className="fas fa-redo me-2"></i>
							<BilingualText translationKey="common.reset" />
						</Button>
						<Button
							variant="primary"
							type="submit"
							className="d-flex align-items-center text-white btn-rounded"
						>
							<i className="fas fa-search me-2"></i>
							<BilingualText translationKey="common.search" />
						</Button>
					</Col>
				</Row>
			)}
		</Form>
	);
};

export default DepositsFilterForm;
